import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiShield, FiUsers, FiScale, FiCheckCircle, FiArrowRight, FiBriefcase, FiFileText, FiGavel } = FiIcons;

function Services() {
  const [heroRef, heroInView] = useInView({ triggerOnce: true, threshold: 0.1 });
  const [servicesRef, servicesInView] = useInView({ triggerOnce: true, threshold: 0.1 });

  const services = [
    {
      icon: FiShield,
      title: 'Corporate Law Services',
      description: 'Our corporate law division handles the full spectrum of business legal needs, from startup formation to complex mergers and acquisitions. We provide strategic legal counsel that enables businesses to grow, adapt, and thrive in South Africa\'s dynamic economic environment.',
      features: [
        'Company formation and structuring',
        'Corporate governance and compliance',
        'Mergers, acquisitions, and joint ventures',
        'Corporate restructuring and rehabilitation',
        'Shareholder agreements and disputes',
        'Directors\' duties and liability',
        'Corporate finance transactions',
        'Regulatory compliance advice'
      ],
      image: 'https://media.phst.co.za/file/gnt-services1.jpg'
    },
    {
      icon: FiUsers,
      title: 'Employment Law Services',
      description: 'We provide comprehensive employment law services to both employers and employees, ensuring compliance with South Africa\'s complex labor legislation while promoting fair and productive workplace relationships.',
      features: [
        'Employment contracts and policies',
        'Disciplinary procedures and hearings',
        'Retrenchment and restructuring processes',
        'Employment equity and transformation planning',
        'BBBEE compliance and implementation',
        'Workplace harassment and discrimination cases',
        'Collective bargaining and union negotiations',
        'CCMA and Labor Court representation'
      ],
      image: 'https://media.phst.co.za/file/gnt-services2.jpg'
    },
    {
      icon: FiScale,
      title: 'Commercial Litigation Services',
      description: 'Our litigation team has extensive experience in resolving complex commercial disputes through negotiation, mediation, arbitration, and court proceedings. We focus on achieving practical, cost-effective solutions that protect our clients\' business interests.',
      features: [
        'Contract disputes and breach claims',
        'Shareholder and partnership disputes',
        'Debt recovery and insolvency matters',
        'Property and landlord-tenant disputes',
        'Intellectual property disputes',
        'Professional negligence claims',
        'Urgent court applications and interdicts',
        'Alternative dispute resolution (mediation and arbitration)'
      ],
      image: 'https://media.phst.co.za/file/gnt-services3.jpg'
    }
  ];

  const additionalServices = [
    {
      icon: FiBriefcase,
      title: 'Business Advisory',
      description: 'Strategic legal advice for business planning and growth'
    },
    {
      icon: FiFileText,
      title: 'Contract Drafting',
      description: 'Professional contract preparation and review services'
    },
    {
      icon: FiGavel,
      title: 'Regulatory Compliance',
      description: 'Comprehensive compliance solutions for all industries'
    }
  ];

  return (
    <div className="overflow-hidden">
      {/* Hero Section */}
      <section ref={heroRef} className="relative bg-gradient-to-br from-navy-800 to-navy-900 text-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={heroInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.8 }}
            className="text-center"
          >
            <h1 className="font-opensans font-light text-4xl lg:text-6xl mb-6 text-white">
              Our Legal <span className="text-gold-600">Services</span>
            </h1>
            <p className="font-opensans text-xl text-gray-200 max-w-3xl mx-auto">
              Comprehensive legal services in Johannesburg including corporate law, employment law, and commercial litigation. Expert attorneys serving Gauteng businesses and individuals.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Main Services Section */}
      <section ref={servicesRef} className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="space-y-20">
            {services.map((service, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 50 }}
                animate={servicesInView ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.8, delay: index * 0.2 }}
                className={`grid grid-cols-1 lg:grid-cols-2 gap-12 items-center ${
                  index % 2 === 1 ? 'lg:grid-flow-col-dense' : ''
                }`}
              >
                <div className={index % 2 === 1 ? 'lg:col-start-2' : ''}>
                  <div className="inline-flex items-center justify-center w-16 h-16 bg-navy-800 text-gold-600 rounded-full mb-6">
                    <SafeIcon icon={service.icon} className="w-8 h-8" />
                  </div>
                  <h2 className="font-playfair font-bold text-3xl text-navy-800 mb-6">
                    {service.title}
                  </h2>
                  <p className="font-opensans text-gray-700 mb-8 leading-relaxed">
                    {service.description}
                  </p>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-8">
                    {service.features.map((feature, featureIndex) => (
                      <div key={featureIndex} className="flex items-start space-x-3">
                        <SafeIcon icon={FiCheckCircle} className="w-5 h-5 text-gold-600 mt-0.5 flex-shrink-0" />
                        <span className="font-opensans text-gray-700 text-sm">{feature}</span>
                      </div>
                    ))}
                  </div>
                  <Link
                    to="/contact"
                    className="inline-flex items-center bg-gold-500 text-navy-800 px-6 py-3 rounded-lg font-opensans font-semibold hover:bg-gold-600 transition-colors duration-200"
                  >
                    Get Consultation
                    <SafeIcon icon={FiArrowRight} className="w-4 h-4 ml-2" />
                  </Link>
                </div>
                <div className={index % 2 === 1 ? 'lg:col-start-1' : ''}>
                  <img
                    src={service.image}
                    alt={service.title}
                    className="rounded-lg shadow-xl w-full h-auto"
                  />
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Additional Services Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="font-playfair font-bold text-4xl text-navy-800 mb-4">
              Additional Services
            </h2>
            <p className="font-opensans text-xl text-gray-600 max-w-3xl mx-auto">
              Beyond our core practice areas, we offer specialized services to meet diverse legal needs.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {additionalServices.map((service, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="bg-white p-8 rounded-lg shadow-lg hover:shadow-xl transition-shadow duration-300 text-center"
              >
                <div className="inline-flex items-center justify-center w-16 h-16 bg-navy-800 text-gold-600 rounded-full mb-6">
                  <SafeIcon icon={service.icon} className="w-8 h-8" />
                </div>
                <h3 className="font-playfair font-bold text-xl text-navy-800 mb-4">
                  {service.title}
                </h3>
                <p className="font-opensans text-gray-700">
                  {service.description}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="font-playfair font-bold text-4xl text-navy-800 mb-4">
              Our Process
            </h2>
            <p className="font-opensans text-xl text-gray-600 max-w-3xl mx-auto">
              We follow a structured approach to ensure the best outcomes for our clients
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            {[
              {
                step: '01',
                title: 'Initial Consultation',
                description: 'We listen to understand your specific legal needs and challenges'
              },
              {
                step: '02',
                title: 'Strategy Development',
                description: 'Our team develops a tailored legal strategy for your situation'
              },
              {
                step: '03',
                title: 'Implementation',
                description: 'We execute the strategy with precision and attention to detail'
              },
              {
                step: '04',
                title: 'Ongoing Support',
                description: 'We provide continued support and guidance throughout the process'
              }
            ].map((process, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="text-center"
              >
                <div className="w-16 h-16 bg-gold-600 text-white rounded-full flex items-center justify-center mx-auto mb-4">
                  <span className="font-playfair font-bold text-lg">{process.step}</span>
                </div>
                <h3 className="font-playfair font-bold text-xl text-navy-800 mb-3">
                  {process.title}
                </h3>
                <p className="font-opensans text-gray-700">
                  {process.description}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-navy-800 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-playfair font-bold text-4xl mb-4">
            Need Legal Assistance?
          </h2>
          <p className="font-opensans text-xl text-gray-200 mb-8 max-w-2xl mx-auto">
            Contact us today to discuss your legal needs. Our experienced team is ready to provide the expert guidance you deserve.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/contact"
              className="bg-gold-500 text-navy-800 px-8 py-4 rounded-lg font-opensans font-semibold hover:bg-gold-600 transition-colors duration-200"
            >
              Schedule Consultation
            </Link>
            <Link
              to="/attorneys"
              className="border-2 border-white text-white px-8 py-4 rounded-lg font-opensans font-semibold hover:bg-white hover:text-navy-800 transition-colors duration-200"
            >
              Meet Our Team
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}

export default Services;